import { generateSetChild, generateAddArrayChild, generateUpdateArrayChild, generateRemoveArrayChild, generateSetMapChild } from '@/helpers/VuexHelpers'
import { service } from '@/service'
import cookie from 'cookie_js'
import { config } from '@/config'
import { dayjs, dayjsServer } from '@/helpers/dayjs'
import { fixProductTaxons } from '@/service/upgradeService'

let cartPromise = null

export default {
	namespaced: true,
	state: {
		items: [],
		initialLoad: false,
		productMap: {},
		basketToken: null,
		basketChannel: "",
		serverData: null,
		loading: false,
		openRequests: 0,
		vouchers: [],
		tip: null,
		surcharges: [],
		deliveryType: 0,
		changedItems: null,
		updatedAt: 0,
		showContinue: true,
	},
	actions: {
		async open({commit, dispatch, state}, {channel, zip, shippingCode, orderNow = null, deliverTime, deliverTimeslot, campaignId, oldToken = null, referralToken = null}) {
			//set channel
			commit('setBasketChannel', { item: channel })
			try {
				let cart = await service
					.carts({ channel: state.basketChannel, zip, shippingCode, orderNow, deliverTime, deliverTimeslot, campaignId, oldToken, referralToken })

				if (cart) {
					await dispatch('readCart', { cart })

					// eslint-disable-next-line require-atomic-updates
					state.initialLoad = true
				}
				commit('setLoading', { item: false })
			} catch (err) {
				commit('setLoading', { item: false })
				throw err
			}
		},
		async load({commit, dispatch, state}, { recalculate = false, accumulatorGroup = null } = {}) {
			if (cartPromise) {
				return cartPromise
			}
			//get a new cart or use existing one
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })

			await dispatch('restoreCart')

			//check for the basket token
			if (state.basketToken == null) {
				throw new Error("no basket token")
			}

			cartPromise = service.cart({ token: state.basketToken, recalculate, accumulatorGroup })
			let cart = await cartPromise
				.catch(err => Promise.resolve({error: true, request: err}))
			cartPromise = null

			if (cart.error) {
				//cart could not be loaded
				if (cart.request && cart.request.response.statusCode == 404) {
					//basket token was not found
					cookie.removeSpecific(config.CART_COOKIE, { path: "/" })
					commit('setBasketToken', { item: null })
				}
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				return false
			} else if (cart) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				await dispatch('readCart', { cart })
				commit('setLoading', { item: false })

				// eslint-disable-next-line require-atomic-updates
				state.initialLoad = true
			}
			return true
		},

		async restoreCart({commit, state}) {
			//read cookie
			let basketCookie = cookie.get(config.CART_COOKIE)
			if (state.basketToken == null && basketCookie) {
				commit('setBasketToken', { item: basketCookie })
			}
		},

		async readCart({commit, state}, { cart }) {
			// TODO: early implementation of deffered cart display
			if (state.openRequests > 0) {
				return
			}
			if (cart) {
				if (typeof cart.tokenValue !== "string") {
					// console.error("cart.readCart: Invalid cart Token", cart)
					throw new Error("Invalid cart Token")
				}
				cookie.set(config.CART_COOKIE, cart.tokenValue, { path: "/" })

				let cartItems = cart.items
				commit('setBasketToken', { item: cart.tokenValue })
				commit('setBasketChannel', { item: cart.channel.code })
				cart.items = null // reduce load for state
				commit('setData', { item: cart })

				const stateItems = [...state.items]; // this is necessary, because otherwise the loop below will skip items when deleting
				stateItems.forEach(item => {
					let exist = cartItems.find(v => v.id == item.id)
					if (!exist) {
						commit("removeItem", { item })
					}
				})

				// we have to collect additional product codes to check if its a product
				let additionalProductCodes = []
				if (config.FEATURE_ADDITIONALPRODUCTS && cart.availableAdditionalProducts) {
					additionalProductCodes = cart.availableAdditionalProducts
						.reduce((prev, rule) => {
							return prev.concat(
								rule.products.map(product => typeof product === 'object' ? product.code: product)
							)
						}, [])
				}

				// read referenced products
				if (cart.products && cart.products.length > 0) {
					cart.products.forEach(p => commit('setProduct' ,{ item: p }))
				}

				// child products
				let childProductsMap = {}
				if (cart.availableChildProducts) {
					childProductsMap = cart.availableChildProducts.reduce((p, c) => {
						// commit('setProduct' ,{ item: c })
						if (typeof c !== 'object') {
							c = state.productMap[c]
						}
						const taxon = cart.childProductsCategories && cart.childProductsCategories.find(cat => cat.taxon == c.taxons.others[0]);
						p[c.code] = {
							code: c.code,
							name: c.variants[0].name,
							description: c.description,
							total: c.variants[0].price.current,
							image: c.images,
							taxons: c.taxons.others,
							type: taxon ? taxon.type : 'extra',
						}
						return p
					}, {})
				}



				cartItems.forEach(item => {
					let exist = state.items.find(v => v.id == item.id)

					if (typeof item.product !== 'object') {
						item.product = state.productMap[item.product]
					}

					// handle tip and strip it out of the service
					if (item.product.isTip) {
						commit('setTip', { item: { total: item.total } })
						return
					}
					fixProductTaxons(item.product)

					// commit('setProduct' ,{ item: item.product })

					let total = item.total
					let variant = item.variantCode != null ? item.product.variants.find(v => v.code === item.variantCode): item.product.variants[0]
					let newItem = {
						id: item.id,
						code: item.product.code,
						name: item.product.name,
						description: item.product.description,
						quantity: item.quantity,
						taxons: item.product.taxons,
						total,
						//server sends another total value for cartdisplay
						// only use total if originalTotal is undefined
						originalTotal: item.originalTotal !== undefined ? item.originalTotal : total,
						//HACK: variant handling as default???
						price: variant.price.current,
						image: item.product.images,
						variant: item.product.name != variant.name ? variant.name : null,
						variantCode: item.product.code != variant.code ? variant.code : item.product.code,
						availableChildProducts: item.product.availableChildProducts || [],
						defaultChildProducts: item.product.defaultChildProducts || [],

						// child products
						children: item.children
							? item.children.map(child => {
									const product = typeof child.product === 'object' ? child.product : state.productMap[child.product]
									const variant = product.variants.length > 1 ? product.variants.find(v => child.variantCode) : product.variants[0]
									return {
										name: product.name,
										code: product.code,
										price: variant.price.current,
										taxons: childProductsMap[product.code] ? childProductsMap[product.code].taxons : [],
										type: childProductsMap[product.code] ? childProductsMap[product.code].type : 'extra',
									}
								})
							: [],

						//debug
						isVariant: variant.code != item.product.code,
						isAdditional: additionalProductCodes.includes(item.product.code),
						isEditable: item.product.isEditable || false, // TODO: first draft of childproducts
						isConfigurable: item.product.isConfigurable || false, // TODO: first draft of childproducts
						isTip: item.product.isTip || false,
						isNew: true, // this is a way to detect if an item was just added now
						unit: item.product.unit || '',
					}
					if (!exist) {
						commit('addItem', { item: newItem })
					} else {
						newItem.isNew = false // on item update we set this to false
						commit('updateItem', { item: newItem })
					}
				})

				commit('setUpdatedAt', { item: Date.now() })
			}
		},

		async addItems({ commit, state, dispatch }, { items, accumulatorGroup = "cart" }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })
			try {
				// fix items
				items.forEach(item => {

				})
				let cart = await service.addCartItems({
					token: state.basketToken,
					items,
					channel: state.basketChannel,
					accumulatorGroup
				})
				if (cart) {
					commit('setOpenRequests', { item: state.openRequests - 1 })
					await dispatch('readCart', { cart })
					commit('setLoading', { item: false })
				}
			} catch(err) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				throw err;
			}

			// try to return items added
			// HACK: we return only the last item
			return state.items.filter(v => v.isNew).pop()
		},

		async updateItem({commit, state, dispatch}, { identifier, quantity, accumulatorGroup = "cart" }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })

			// we remove the item directly bevor the service has finished
			// if (quantity <= 0) {
			// 	commit("removeItem", { item: { id: identifier } })
			// } else {
			// 	let item = state.items.find(v => v.id == identifier)
			// 	item.quantity = quantity
			// 	commit("updateItem", { item: item })
			// }

			try {
				let cart = await (
					quantity <= 0
					? service.removeCartItem({
							token: state.basketToken,
							identifier,
							channel: state.basketChannel,
							accumulatorGroup
						})
					: service.updateCartItem({
							token: state.basketToken,
							identifier, quantity,
							channel: state.basketChannel,
							accumulatorGroup
						})
				)
				if (cart) {
					commit('setOpenRequests', { item: state.openRequests - 1 })
					await dispatch('readCart', { cart })
					commit('setLoading', { item: false })
				}
			} catch(err) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				// console.error(err)
				throw err;
			}
		},

		async removeItem({commit, state, dispatch}, { identifier, accumulatorGroup = "cart" }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })
			try {
				let cart = await service.removeCartItem({ token: state.basketToken, identifier, channel: state.basketChannel, group: accumulatorGroup})
				if (cart) {
					commit('setOpenRequests', { item: state.openRequests - 1 })
					await dispatch('readCart', { cart })
					commit('setLoading', { item: false })
				}
			} catch(err) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				// console.error(err)
				throw err;
			}
		},

		async copyItem({ commit, state, dispatch}, { identifier, accumulatorGroup = 'cart' }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })

			try {
				const item = state.items.find(v => v.id == identifier)
				const items = [
					{
							"quantity": 1,
							"productCode": item.code,
							"variantCode": item.variantCode,
							"childCodes": item.children.map(v => v.code),
							"forceCopy": true,
					}
				]
				let cart = await service.addCartItems({ token: state.basketToken, items, channel: state.basketChannel, group: accumulatorGroup})
				commit('setOpenRequests', { item: state.openRequests - 1 })
				if (cart) {
					await dispatch('readCart', { cart })
				}
				commit('setLoading', { item: false })
			} catch (err) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				commit('setLoading', { item: false })
				// console.error(err)
				throw err;
			}
		},

		async addCoupon({commit, state, dispatch}, {coupon, email}) {
			commit('setLoading', { item: true })
			try {
				let cart = await service.addCoupon({token: state.basketToken, coupon, email, channel: state.basketChannel})
				if (cart) {
					await dispatch('readCart', { cart })
				}
				commit('setLoading', { item: false })
				return {};
			} catch(err) {
				// console.error(err)
				commit('setLoading', { item: false })
				throw err;
			}
		},

		async removeCoupon({commit, state, dispatch}) {
			commit('setLoading', { item: true })
			let cart = await service.removeCoupon({token: state.basketToken, channel: state.basketChannel})
			if (cart) {
				await dispatch('readCart', { cart })
				commit('setLoading', { item: false })
			}
		},

		async replaceCartItemChildren({ commit, state, dispatch }, { identifier, childCodes, accumulatorGroup = "cart" }) {
			commit('setLoading', { item: true })
			commit('setOpenRequests', { item: state.openRequests + 1 })
			let cart = await service.replaceCartItemChildren({ token: state.basketToken, identifier, childCodes, channel: state.basketChannel, group: accumulatorGroup})
			if (cart) {
				commit('setOpenRequests', { item: state.openRequests - 1 })
				await dispatch('readCart', { cart })
				commit('setLoading', { item: false })
			}
		},

		async exists({commit, state, dispatch}, {token, accumulatorGroup = null} = {}) {
			if (token == null) {
				token = state.basketToken
			}
			if (token == null) {
				//read cookie
				const basketCookie = cookie.get(config.CART_COOKIE)
				if (basketCookie) {
					token = basketCookie
					commit('setBasketToken', { item: basketCookie })
				}
			}
			if (!token) {
				return Promise.reject(Error("Cart::exists no basketToken"))
			}

			let result = await dispatch('load', {token, accumulatorGroup})
			if (!result) {
				return Promise.reject(Error("Cart::exists no valid basket"))
			}

		},
		async restoreOrder({ commit, dispatch, state }, { token }) {
			let result = await service.orderRestore({ token, channel: state.basketChannel, oldToken: state.basketToken })
			commit('setBasketToken', { item: result.tokenValue })
			if (result.notAvailableItems) {
				const changedItems = result.notAvailableItems
					.map(v => ({
						id: v.id,
						name: v.product.name,
						image: v.product.images.length && v.product.images[0].path || "",
					}))
				commit('setChangedItems', { item: changedItems })
			}
			return result
		},
		async updateDeliverTime({ commit, dispatch, state }, { orderNow, deliverTime, deliverTimeslot, deliverMethod, productCode, confirmChange }) {
			commit('setLoading', { item: true })
			try {
				deliverTime = orderNow ? null : deliverTime; // the server might return an error if deliverTime is set when orderNow is true
				let result = await service.updateDeliverTime({ orderNow, deliverTime, deliverTimeslot, deliverMethod, productCode, confirmChange, token: state.basketToken, channel: state.basketChannel })
				if (result.cart) {
					commit('setChangedItems', { item: null })
					await dispatch('readCart', { cart: result.cart })
				}
				commit('setLoading', { item: false })
			} catch(err) {
				commit('setLoading', { item: false })
				if (err.response.data.notAvailableItems) {
					const changedItems = err.response.data.notAvailableItems
						.map(v => ({
							id: v.id,
							name: v.product.name,
							image: v.product.images.length && v.product.images[0].path || "",
						}))
					commit('setChangedItems', { item: changedItems })
				}
				throw err
			}
		},

		adjustTip({ commit }, { value }) {
			commit('setTip', { item: { total: value } })
		},
		showContinue({ commit }, { value }) {
			commit('setShowContinue', { item: value });
		},
	},
	getters: {
		items(state) {
			return state.items
		},
		total(state) {
			let total = 0
			if (state.serverData) {
				const {
					totals: { total: serviceTotal, totalWithoutTip },
				} = state.serverData

				total = totalWithoutTip || serviceTotal
			}
			return total
		},
		subtotal(state, getters) {
			return getters.total - getters.deliveryPrice;
		},
		subtotalBeforePromotion(state, getters) {
			if (!state.serverData) {
				return 0
			}

			const {
				totals: { items = 0, promotion = 0 },
			} = state.serverData

			return items + Math.abs(promotion)
		},
		totalItems(state) {
			return state.items
				.reduce((p, c) => p + c.quantity, 0)
		},
		// TODO: find a better solution for this
		totalItemsWithoutAdditional(state) {
			return state.items
				.reduce((p, c) => c.isAdditional ? p : p + c.quantity, 0)
		},
		hasBasket(state) {
			return !!state.serverData
		},
		basketState(state) {
			return state.serverData
				? state.serverData.checkoutState
				: "none"
		},
		discounts(state) {
			return state.serverData
				? Object.values(state.serverData.cartDiscounts)
				: []
		},
		discountCode(state) {
			return state.serverData
				? state.serverData.couponCode
				: null
		},
		deliveryPrice(state) {
			return state.serverData
				? state.serverData.totals.shipping
				: 0
		},
		deliveryTime(state) {
			//in minutes
			return state.serverData
				? state.serverData.zip
					? state.serverData.zip.deliveryTime
					: 0
				: 0
		},
		minCartPrice(state) {
			return state.serverData
				? state.serverData.zip?.minDeliveryAmount
					? state.serverData.zip.minDeliveryAmount * 100
					: 0
				: 0
		},
		isCartValidForCheckout(state, getters) {
			let method = getters.shippingMethod
			if (method && ['delivery', 'planzer', 'uk'].includes(method.code)) {
				return getters.totalItemsWithoutAdditional > 0
					&& getters.minCartPrice <= (state.serverData.totals.items + Math.abs(state.serverData.totals.promotion)) // HACK: this doesn't seem like a very good solution ;)
			}
			//currently we check for the
			return getters.totalItemsWithoutAdditional > 0
		},
		minCartFreePrice(state) {
			return state.serverData
			? state.serverData.zip?.minBasketFree
				? state.serverData.zip.minBasketFree * 100
				: 0
			: 0
		},
		shippingMethod(state) {
			return state.serverData?.shipments[0]?.method || null
		},
		shopLocation(state) {
			return state.serverData
				? state.serverData.channel
				: null
		},
		channelName(state) {
			return state.serverData
				? state.serverData.channel.name
				: null
		},
		cartIncludesAlcohol(state) {
			return state.serverData
				? state.serverData.alcoholCheck == true
				: false
		},
		cartDeliveryTime(state) {
			return state.serverData
				? dayjsServer(state.serverData.deliverTime)
				: null
		},
		cartDeliveryTimeslot(state) {
			return state.serverData
				? state.serverData.deliverTimeslot
				: null
		},
		cartTip (state, getters) {
			if (state.tip) {
				return state.tip.total
			}
			return getters.cartTipDefault
		},
		cartTipEnabled (state) {
			return state.serverData
				? (state.serverData.channel.location.tipEnabled || false)
				: null
		},
		cartTipDefault (state) {
			return state.serverData
				? (state.serverData.channel.location.defaultTip || 0)
				: null
		},
		cartChangedItems(state) {
			// TODO: currently its possible that the cartitem was removed and so the reference is broken
			return state.changedItems
				?	state.changedItems
						.map(v => ({
							...v,
							item: state.items.find(item => item.id == v.id)
						}))
				: []
		},
		cartOrderNow(state) {
			return state.serverData
				? state.serverData.orderNow
				: false
		},
		cartOrderNowAvailable(state) {
			return state.serverData
				? state.serverData.orderNowAvailable
				: false
		},
		cartUpdateAt(state) {
			return state.updatedAt
		},
		cartLoading(state) {
			return state.loading
		},
		cartOrderTimePassed(state, getters) {
			// the basket was with order now, but its not available anymore
			// currently this works only with order now but can be extended with deliveryTime and timeline
			return getters.cartOrderNow == true && getters.cartOrderNowAvailable == false
		},
		showContinue(state) {
			return !!state.showContinue;
		},
		cartCrossSell(state) {
			return state.serverData
				? state.serverData.crossSell
				: []
		},
		cartZip(state) {
			return state.serverData?.zip?.zip || ''
		}
	},
	mutations: {
		setData: generateSetChild("serverData"),
		addItem: generateAddArrayChild("items"),
		updateItem: generateUpdateArrayChild("items"),
		removeItem: generateRemoveArrayChild("items"),
		setBasketToken: generateSetChild("basketToken"),
		setBasketChannel: generateSetChild("basketChannel"),
		setLoading: generateSetChild("loading"),
		setOpenRequests: generateSetChild("openRequests"),
		setChangedItems: generateSetChild("changedItems"),
		setUpdatedAt: generateSetChild("updatedAt"),
		setTip: generateSetChild('tip'),
		setProduct: generateSetMapChild('productMap', 'code'),
		setShowContinue: generateSetChild('showContinue'),
	}
}
